/**
 * Data plane — Datasets (L1, ADR-0026).
 *
 * Lists every registered surface, **grouped by owner** (the owning app for
 * internal datasets, the source for external ones) so the catalog stays
 * readable as it grows — a flat list of every app table doesn't scale. A
 * toolbar adds free-text search + a binding filter. Each owner is a single
 * clickable row; clicking it opens a drawer listing that owner's datasets
 * (the detail items) with their publish toggle + actions. A Preview action
 * runs the uniform query seam; the published toggle is the Odino-visibility
 * switch (Block 7). Auto datasets can't be renamed or deleted.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BookText,
  Boxes,
  ChevronRight,
  Database,
  Filter,
  Network,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { DatasetPreviewDialog } from "@/components/datasets/DatasetPreviewDialog";
import { DatasetFilterDialog } from "@/components/datasets/DatasetFilterDialog";
import { DatasetQueryBuilder } from "@/components/datasets/DatasetQueryBuilder";
import { FederationHintBadge } from "@/components/datasets/FederationHintBadge";
import { TagList } from "@/components/tags";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DescriptionTooltip } from "@/components/ui/description-tooltip";
import { Input } from "@/components/ui/input";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { cn } from "@/lib/cn";
import { datasetsApi, type Dataset, type DatasetBinding } from "@/lib/api-datasets";
import { usePermissions } from "@/lib/permissions";

type BindingFilter = "all" | DatasetBinding;

export interface DatasetGroup {
  /** Stable key — binding + owner (an app and a source could share a name). */
  key: string;
  owner: string;
  binding: DatasetBinding;
  datasets: Dataset[];
}

/**
 * Pure grouping for the catalog UI: filter by free-text (name/owner) +
 * binding, then bucket by owner. Internal groups (apps) come first, then
 * external (sources); both alphabetical. Exported for unit tests.
 */
export function groupDatasets(
  rows: Dataset[],
  opts: { search: string; binding: BindingFilter },
): DatasetGroup[] {
  const q = opts.search.trim().toLowerCase();
  const filtered = rows.filter((d) => {
    if (opts.binding !== "all" && d.binding !== opts.binding) return false;
    if (!q) return true;
    return (
      d.name.toLowerCase().includes(q) ||
      d.owner.toLowerCase().includes(q) ||
      (d.tags ?? []).some((t) => t.toLowerCase().includes(q))
    );
  });

  const byKey = new Map<string, DatasetGroup>();
  for (const d of filtered) {
    const key = `${d.binding}:${d.owner}`;
    let g = byKey.get(key);
    if (!g) {
      g = { key, owner: d.owner, binding: d.binding, datasets: [] };
      byKey.set(key, g);
    }
    g.datasets.push(d);
  }

  const order = (b: DatasetBinding) => (b === "internal" ? 0 : 1);
  return [...byKey.values()]
    .map((g) => ({
      ...g,
      datasets: [...g.datasets].sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => order(a.binding) - order(b.binding) || a.owner.localeCompare(b.owner));
}

/** IT fallback labels for a dataset's physical `kind` (i18n keys override). */
const KIND_FALLBACK: Record<string, string> = {
  table: "tabella",
  view: "vista",
  collection: "collezione",
  file: "file",
  sheet: "foglio",
  object: "oggetto",
  index: "indice",
};

/** Drop the redundant "Owner · " prefix from a dataset's name inside its group. */
function rowLabel(name: string, owner: string): string {
  const prefix = `${owner} · `;
  return name.startsWith(prefix) ? name.slice(prefix.length) : name;
}

export function DatasetsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can("datasets", "write");
  const canDelete = can("datasets", "delete");
  const canManage = canWrite || canDelete;

  const [createOpen, setCreateOpen] = React.useState(false);
  const [previewTarget, setPreviewTarget] = React.useState<Dataset | null>(null);
  // The dataset whose ADR-0028 N3 row filter is being edited.
  const [filterTarget, setFilterTarget] = React.useState<Dataset | null>(null);
  // The dataset whose ADR-0063 P5 query MODEL (join graph + shape) is being edited
  // on the visual canvas. Same source → pushed-down JOIN, different → federated.
  const [queryBuilderTarget, setQueryBuilderTarget] = React.useState<Dataset | null>(null);
  // The dataset being renamed (external only — auto datasets are name-locked).
  const [renameTarget, setRenameTarget] = React.useState<Dataset | null>(null);
  const [renameValue, setRenameValue] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [binding, setBinding] = React.useState<BindingFilter>("all");
  // Drawer holds the owner's KEY (not the object) so the detail stays live
  // after a publish toggle refetches the list. Null = drawer closed.
  const [drawerKey, setDrawerKey] = React.useState<string | null>(null);

  const listQ = useQuery({ queryKey: ["datasets"], queryFn: () => datasetsApi.list() });

  const publish = useMutation({
    mutationFn: ({ id, published }: { id: string; published: boolean }) =>
      datasetsApi.update(id, { published }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["datasets"] }),
  });
  const remove = useMutation({
    mutationFn: datasetsApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["datasets"] }),
  });
  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      datasetsApi.update(id, { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["datasets"] });
      setRenameTarget(null);
    },
  });
  // Re-introspect the live schema → updates drift + last_introspected_at (B3.1).
  const introspect = useMutation({
    mutationFn: datasetsApi.introspect,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["datasets"] }),
  });

  const rows = listQ.data ?? [];
  const groups = React.useMemo(
    () => groupDatasets(rows, { search, binding }),
    [rows, search, binding],
  );
  const drawerGroup = drawerKey ? groups.find((g) => g.key === drawerKey) ?? null : null;
  const DrawerIcon = drawerGroup ? (drawerGroup.binding === "internal" ? Boxes : Database) : null;

  const FILTERS: { value: BindingFilter; labelKey: string; fallback: string }[] = [
    { value: "all", labelKey: "datasets.filter.all", fallback: "Tutti" },
    { value: "internal", labelKey: "datasets.filter.internal", fallback: "Interni" },
    { value: "external", labelKey: "datasets.filter.external", fallback: "Esterni" },
  ];

  const bindingLabel = (b: DatasetBinding) =>
    b === "internal"
      ? t("datasets.binding.internal", "interno")
      : t("datasets.binding.external", "esterno");

  // Physical form (ADR-0027) — table/view/collection/file/sheet/object/index.
  // Free string server-side; fall back to the raw value for an unknown kind.
  const kindLabel = (k: string) => t(`datasets.kind.${k}`, KIND_FALLBACK[k] ?? k);

  return (
    <PageContainer>
      <PageHeader
        title={t("datasets.title", "Dataset")}
        description={t(
          "datasets.subtitle",
          "Superfici interrogabili sopra le sorgenti. Le tabelle delle app si registrano da sole; aggiungi dataset esterni a mano. Pubblica per renderli visibili a Odino.",
        )}
        actions={
          <>
            <Button variant="outline" size="sm" asChild>
              <Link to="/datasets/graph">
                <Network className="h-4 w-4" strokeWidth={1.5} />
                {t("relationships.graphTitle", "Grafo dati")}
              </Link>
            </Button>
            {canWrite && (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                {t("datasets.new", "Nuovo dataset")}
              </Button>
            )}
          </>
        }
      />
      <PageContent>
        {rows.length === 0 && !listQ.isLoading ? (
          <EmptyState
            icon={Database}
            title={t("datasets.empty", "Nessun dataset")}
            description={t(
              "datasets.emptyDescription",
              "Crea una tabella in un'app (si registra da sola) o collega una tabella di una sorgente esterna.",
            )}
          />
        ) : (
          <div className="space-y-3">
            {/* Toolbar: search + binding segmented filter */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative max-w-xs flex-1">
                <Search
                  className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("datasets.searchPlaceholder", "Cerca dataset…")}
                  className="pl-8"
                />
              </div>
              <div className="inline-flex items-center rounded-md border border-border bg-surface p-0.5">
                {FILTERS.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => setBinding(f.value)}
                    className={cn(
                      "rounded px-2.5 py-1 text-xs font-medium transition-colors",
                      binding === f.value
                        ? "bg-elevated text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {t(f.labelKey, f.fallback)}
                  </button>
                ))}
              </div>
            </div>

            {groups.length === 0 ? (
              <p className="rounded-lg border border-border/60 px-3 py-8 text-center text-xs text-muted-foreground">
                {t("datasets.noMatch", "Nessun dataset corrisponde ai filtri.")}
              </p>
            ) : (
              // Owners as a flat clickable list — no more accordions. Clicking
              // a row opens the drawer with that owner's datasets.
              <div className="divide-y divide-border/60 overflow-hidden rounded-lg border border-border/60">
                {groups.map((g) => {
                  const OwnerIcon = g.binding === "internal" ? Boxes : Database;
                  return (
                    <button
                      key={g.key}
                      type="button"
                      onClick={() => setDrawerKey(g.key)}
                      className="flex w-full items-center gap-2 bg-surface px-3 py-2.5 text-left transition-colors hover:bg-elevated/50"
                    >
                      <OwnerIcon className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                      <span className="truncate text-sm font-medium">{g.owner}</span>
                      <Badge
                        tone={g.binding === "internal" ? "active" : "neutral"}
                        className="text-[10px]"
                      >
                        {bindingLabel(g.binding)}
                      </Badge>
                      <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                        {t("datasets.countLabel", "{{count}} dataset", { count: g.datasets.length })}
                      </span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={2} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </PageContent>

      {/* Detail drawer — the owner's datasets. */}
      <Sheet open={!!drawerGroup} onOpenChange={(o) => !o && setDrawerKey(null)}>
        {drawerGroup && (
          <SheetContent width="w-[44rem]">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2 text-sm font-semibold text-foreground">
                {DrawerIcon && (
                  <DrawerIcon className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                )}
                {drawerGroup.owner}
                <Badge
                  tone={drawerGroup.binding === "internal" ? "active" : "neutral"}
                  className="text-[10px]"
                >
                  {bindingLabel(drawerGroup.binding)}
                </Badge>
              </SheetTitle>
              <SheetDescription className="text-xs text-muted-foreground">
                {t("datasets.countLabel", "{{count}} dataset", {
                  count: drawerGroup.datasets.length,
                })}
              </SheetDescription>
            </SheetHeader>
            <SheetBody className="p-0">
              <Table>
                <Thead>
                  <Tr>
                    <Th>{t("datasets.col.name", "Nome")}</Th>
                    <Th className="w-28">{t("datasets.col.published", "Pubblicato")}</Th>
                    <Th>{t("datasets.col.tags", "Tag")}</Th>
                    {canManage && (
                      <Th className="w-32 text-right">{t("datasets.col.actions", "Azioni")}</Th>
                    )}
                  </Tr>
                </Thead>
                <Tbody>
                  {drawerGroup.datasets.map((d) => (
                    <Tr key={d.id}>
                      <Td>
                        <div className="flex items-center gap-2 font-medium">
                          {rowLabel(d.name, d.owner)}
                          <Badge tone="neutral" className="text-[10px] font-normal">
                            {kindLabel(d.kind)}
                          </Badge>
                          {d.schema_drifted && (
                            <Badge
                              tone="warning"
                              className="text-[10px]"
                              title={t("datasets.driftHint", "Lo schema è cambiato dall'ultima introspezione")}
                            >
                              {t("datasets.drifted", "schema cambiato")}
                            </Badge>
                          )}
                          <DescriptionTooltip text={d.description} />
                          <FederationHintBadge
                            datasetId={d.id}
                            enabled={
                              !!(d.config as { model?: { joins?: unknown[] } } | null)?.model?.joins
                                ?.length
                            }
                          />
                        </div>
                        {(d.row_count !== null || d.last_updated) && (
                          <div className="mt-0.5 text-[11px] text-muted-foreground">
                            {d.row_count !== null &&
                              t("datasets.rowCount", "{{formatted}} righe", {
                                count: d.row_count,
                                formatted: d.row_count.toLocaleString(),
                              })}
                            {d.row_count !== null && d.last_updated && " · "}
                            {d.last_updated &&
                              t("datasets.lastUpdated", "agg. {{when}}", {
                                when: new Date(d.last_updated).toLocaleDateString(),
                              })}
                          </div>
                        )}
                      </Td>
                      <Td>
                        {canWrite ? (
                          <Switch
                            checked={d.published}
                            onCheckedChange={(v) => publish.mutate({ id: d.id, published: v })}
                          />
                        ) : (
                          <Badge tone={d.published ? "active" : "neutral"}>
                            {d.published
                              ? t("datasets.public", "pubblico")
                              : t("datasets.private", "privato")}
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <TagList tags={d.tags} max={3} />
                      </Td>
                      {canManage && (
                        <Td className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {canWrite && (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={t("datasets.action.introspect", "Re-introspeziona lo schema")}
                                title={t("datasets.action.introspect", "Re-introspeziona lo schema")}
                                disabled={introspect.isPending}
                                onClick={() => introspect.mutate(d.id)}
                              >
                                <RefreshCw
                                  className={cn(
                                    "h-3.5 w-3.5",
                                    introspect.isPending && introspect.variables === d.id && "animate-spin",
                                  )}
                                  strokeWidth={1.5}
                                />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={t("datasets.action.semantics", "Semantica")}
                              title={t("datasets.action.semantics", "Semantica")}
                              onClick={() => navigate(`/datasets/${d.id}/semantics`)}
                            >
                              <BookText className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </Button>
                            {canWrite && (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={t("datasets.action.rename", "Rinomina")}
                                title={t("datasets.action.rename", "Rinomina")}
                                onClick={() => {
                                  setRenameValue(d.name);
                                  setRenameTarget(d);
                                }}
                              >
                                <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canWrite && (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={t("datasets.action.filter", "Filtro righe")}
                                title={t("datasets.action.filter", "Filtro righe")}
                                onClick={() => setFilterTarget(d)}
                              >
                                <Filter
                                  className={cn(
                                    "h-3.5 w-3.5",
                                    (d.config as { filter?: unknown } | null)?.filter
                                      ? "text-accent"
                                      : "",
                                  )}
                                  strokeWidth={1.5}
                                />
                              </Button>
                            )}
                            {canWrite && (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={t("datasets.action.queryBuilder", "Query builder")}
                                title={t("datasets.action.queryBuilder", "Query builder (canvas)")}
                                onClick={() => setQueryBuilderTarget(d)}
                              >
                                <Network
                                  className={cn(
                                    "h-3.5 w-3.5",
                                    (d.config as { model?: { joins?: unknown[] } } | null)?.model
                                      ?.joins?.length
                                      ? "text-accent"
                                      : "",
                                  )}
                                  strokeWidth={1.5}
                                />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={t("datasets.action.preview", "Anteprima")}
                              title={t("datasets.action.preview", "Anteprima dati")}
                              onClick={() => setPreviewTarget(d)}
                            >
                              <Search className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </Button>
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={t("common.delete", "Elimina")}
                                onClick={() => {
                                  if (
                                    confirm(
                                      t("datasets.deleteConfirm", `Eliminare il dataset "${d.name}"?`),
                                    )
                                  )
                                    remove.mutate(d.id);
                                }}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                              </Button>
                            )}
                          </div>
                        </Td>
                      )}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </SheetBody>
          </SheetContent>
        )}
      </Sheet>

      {/* Canvas-first CREATE: "Nuovo dataset" opens the query builder empty — pick the
          primary source IN the canvas, add joins/shape, and the object is born on Save
          (no separate create dialog). */}
      {createOpen && (
        <DatasetQueryBuilder dataset={null} open onClose={() => setCreateOpen(false)} />
      )}
      <DatasetPreviewDialog
        dataset={previewTarget}
        onOpenChange={(open) => !open && setPreviewTarget(null)}
      />
      {filterTarget && (
        <DatasetFilterDialog
          dataset={filterTarget}
          open
          onClose={() => setFilterTarget(null)}
        />
      )}
      {queryBuilderTarget && (
        <DatasetQueryBuilder
          dataset={queryBuilderTarget}
          open
          onClose={() => setQueryBuilderTarget(null)}
        />
      )}

      {/* Rename — an external dataset's name is free; renaming the second
          dataset on a table off its auto-default name is how you keep e.g.
          Clienti + Fornitori on ONE source table (each with its own filter). */}
      <Dialog
        open={!!renameTarget}
        onOpenChange={(open) => {
          if (!open) {
            setRenameTarget(null);
            rename.reset();
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold">
              {t("datasets.rename.title", "Rinomina dataset")}
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const name = renameValue.trim();
              if (name && renameTarget && name !== renameTarget.name) {
                rename.mutate({ id: renameTarget.id, name });
              }
            }}
          >
            <DialogBody className="space-y-2">
              <Input
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder={t("datasets.rename.placeholder", "Nuovo nome")}
                autoFocus
              />
              {rename.error && (
                <p className="text-xs text-danger">{(rename.error as Error).message}</p>
              )}
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setRenameTarget(null)}>
                {t("common.cancel", "Annulla")}
              </Button>
              <Button
                type="submit"
                disabled={
                  rename.isPending ||
                  !renameValue.trim() ||
                  renameValue.trim() === renameTarget?.name
                }
              >
                {t("common.save", "Salva")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
